import { ReactNode } from 'react';

export type LayoutProps<T extends {} = {}> = {
  params: T & {
    lang: string;
  };
  children: ReactNode;
};

export type PageProps<Params extends {} = {}, SearchParams extends {} = {}> = {
  params: Params & {
    lang: string;
  };
  searchParams: SearchParams;
};

export type Nullable<T> = T | null | undefined;
export type DeepNullable<T> = {
  [P in keyof T]: T[P] extends object
    ? Nullable<DeepNullable<T[P]>>
    : Nullable<T[P]>;
};

export type AnalyticsProps = {
  analyticsParentId: string;
  analyticsElementId?: string;
  analyticsType?: string;
  analyticsContext?: Record<string, any>;
};

export type PaginatedApiData<T> = {
  data: T[];
  nextToken: string | null | undefined;
};

export type User = {
  // identification attributes
  'cognito:username': string;
  'cognito:groups'?: UserRole[];
  id: string;
  sub: string;

  // custom attributes
  'custom:uuid'?: string;
  'custom:profile_completed'?: string;
  'custom:community_terms_ack'?: string;

  // standard mandatory attributes
  email: string;

  // standard optional attributes
  address?: { formatted: string };
  email_verified?: boolean;
  family_name?: string;
  given_name?: string;
  locale?: string;
  name?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  picture?: string;
  zoneinfo?: string;

  // standard attributes not-enabled in nimbus yet
  birthdate?: string;
  gender?: string;
  middle_name?: string;
  nickname?: string;
  preferred_username?: string;
  profile?: string;
  website?: string;

  // social identities
  identities: Array<{
    dateCreated: string;
    issuer?: string | null;
    primary: boolean;
    providerName: string;
    providerType: string;
    userId: string;
  }>;
};

export type UserInfoAddress = {
  state?: string | null | undefined;
  country?: string | null | undefined;
  zipcode?: string | null | undefined;
};

export enum UserRole {
  Admin = 'admin',
  Dev = 'dev',
  Content = 'content'
}

export enum AuthFlowType {
  ADMIN_NO_SRP_AUTH = 'ADMIN_NO_SRP_AUTH',
  ADMIN_USER_PASSWORD_AUTH = 'ADMIN_USER_PASSWORD_AUTH',
  CUSTOM_AUTH = 'CUSTOM_AUTH',
  REFRESH_TOKEN = 'REFRESH_TOKEN',
  REFRESH_TOKEN_AUTH = 'REFRESH_TOKEN_AUTH',
  USER_AUTH = 'USER_AUTH',
  USER_PASSWORD_AUTH = 'USER_PASSWORD_AUTH',
  USER_SRP_AUTH = 'USER_SRP_AUTH'
}

export enum FeatureGateEnum {
  BOOKMARKS = 'bookmarks',
  RECURRING_MEETUPS = 'recurring_meetups',
  NOTIFICATIONS = 'notifications',
  DASHBOARD_SEGMENTATION = 'dashboard_segmentation',
  WEB_RUDDERSTACK_BEACON = 'web_rudderstack_beacon',
  PROFILE_COMPLETE_SUBSCRIBE_TO_SMS = 'profile_complete_subscribe_to_sms',
  DASHBOARD_TASK_COMMENTS = 'dashboard_task_comments',
  STREAK_CELEBRATION = 'streak_celebration',
  FIVE_TO_THRIVE_COMMENT_COUNT = '5_to_thrive_comment_count',
  DASHBOARD_ONBOARDING_TOUR = 'dashboard_onboarding_tour',
  MORNING_MINDSET_PROMPT = 'morning_mindset_prompt',
  NEW_GROWTH_COINS_GRANT = 'new_growth_coins_grant',
  GROWTH_COINS_SUMMARY = 'growth_coins_summary',
  MINI_PROFILE = 'mini_profile',
  SMART_BANNERS = 'smart_banners',
  CANCEL_FLOW = 'cancel_flow_3.0',
  HERO_PURCHASE_TAB = 'hero_tabs_for_purchases',
  WATCH_NEXT_TAB = 'hero_tabs_for_watch_next',
  LEARN_FILTERS = 'learn_filters',
  CHECKLIST_SORTING = 'checklist_sorting'
}

export enum ExperimentEnum {
  DASHBOARD_SEGMENTATION = 'dashboard_segmentation_experiment',
  DASHBOARD_SEGMENTATION_BANNER_CONTENT = 'dashboard_segmentation_banner_content'
}

export enum DynamicConfigEnum {
  CANCEL_FLOW_MAPPING = 'cancel_flow_mapping'
}

export enum AdjustDeeplinkMappingEnum {
  CAMPAIGN_DAILY_FIRE = 'CampaignDailyFire',
  DASHBOARD = 'Dashboard',
  DASHBOARD_DAILY_FIRE = 'DashboardDailyFire',
  DASHBOARD_FEATURED_VIDEO = 'DashboardFeaturedVideo',
  DASHBOARD_MORNING_MINDSET = 'DashboardMorningMindset',
  DASHBOARD_CHECKLIST = 'DashboardChecklist',
  DASHBOARD_DAILY_REFLECTION = 'DashboardDailyReflection',
  BRENDON_AI_OVERVIEW = 'BrendonAiOverview',
  BRENDON_AI_VOICE = 'BrendonAiVoice',
  BRENDON_AI_CHAT = 'BrendonAiChat',
  MEETUP = 'Meetup',
  MEETUP_CREATE = 'MeetupCreate',
  COMMUNITY_FEED = 'CommunityFeed',
  GENERAL_CHAT = 'GeneralChat',
  LIFE_SCORES = 'LifeScores',
  JOURNAL = 'Journal',
  AMBASSADORS = 'Ambassadors',
  USER_PROFILE = 'UserProfile',
  CHECKLISTS = 'Checklists',
  NOTES = 'Notes',
  MEMBERSHIP_AND_BILLING = 'MembershipAndBilling',
  LEARN = 'Learn',
  COURSE = 'Course',
  EVENT = 'Event',
  REPLAY = 'Replay',
  CHALLENGE = 'Challenge',
  LEADERBOARD_STREAKS = 'LeaderboardStreaks',
  LEADERBOARD_COINS = 'LeaderboardCoins',
  LEADERBOARD_AMBASSADORS = 'LeaderboardAmbassadors'
}

export type DeviceSelectors = {
  isBrowser: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isSmartTV: boolean;
  isConsole: boolean;
  isWearable: boolean;
  isEmbedded: boolean;
  isMobileSafari: boolean;
  isChromium: boolean;
  isMobileOnly: boolean;
  isAndroid: boolean;
  isWinPhone: boolean;
  isIOS: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isSafari: boolean;
  isOpera: boolean;
  isIE: boolean;
  isEdge: boolean;
  isYandex: boolean;
  osVersion: string;
  osName: string;
  fullBrowserVersion: string;
  browserVersion: string;
  browserName: string;
  mobileVendor: string;
  mobileModel: string;
  engineName: string;
  engineVersion: string;
  getUA: string;
  deviceType: string;
  isIOS13: boolean;
  isIPad13: boolean;
  isIPhone13: boolean;
  isIPod13: boolean;
  isElectron: boolean;
  isEdgeChromium: boolean;
  isLegacyEdge: boolean;
  isWindows: boolean;
  isMacOs: boolean;
  isMIUI: boolean;
  isSamsungBrowser: boolean;
};
